import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Home, 
  TrendingUp, 
  TrendingDown,
  Minus,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Loader2
} from "lucide-react";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";

interface PlacementStats {
  total: number;
  active: number;
  pending: number;
  completed: number;
  cancelled: number;
  thisMonth: number;
  lastMonth: number;
  avgDurationDays: number;
}

export function PlacementStatsWidget() {
  const [stats, setStats] = useState<PlacementStats>({
    total: 0,
    active: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    thisMonth: 0,
    lastMonth: 0,
    avgDurationDays: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();

    // Real-time subscription for placements
    const channel = supabase
      .channel('placement-stats')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'placements' }, () => {
        fetchStats();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchStats = async () => {
    try {
      const { data: placements, error } = await supabase
        .from("placements")
        .select("id, status, start_date, end_date, created_at");

      if (error) throw error;

      const now = new Date();
      const thisMonthStart = startOfMonth(now);
      const thisMonthEnd = endOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));

      const active = placements?.filter(p => p.status === "active").length || 0;
      const pending = placements?.filter(p => p.status === "pending").length || 0;
      const completed = placements?.filter(p => p.status === "completed").length || 0;
      const cancelled = placements?.filter(p => p.status === "cancelled").length || 0;

      const thisMonth = placements?.filter(p => {
        const createdAt = new Date(p.created_at);
        return createdAt >= thisMonthStart && createdAt <= thisMonthEnd;
      }).length || 0;

      const lastMonth = placements?.filter(p => {
        const createdAt = new Date(p.created_at);
        return createdAt >= lastMonthStart && createdAt <= lastMonthEnd;
      }).length || 0;

      // Calculate average duration for completed placements
      const completedPlacements = placements?.filter(p => p.status === "completed" && p.end_date) || [];
      let avgDuration = 0;
      if (completedPlacements.length > 0) {
        const totalDays = completedPlacements.reduce((sum, p) => {
          const start = new Date(p.start_date);
          const end = new Date(p.end_date!);
          return sum + Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        }, 0);
        avgDuration = Math.round(totalDays / completedPlacements.length);
      }

      setStats({
        total: placements?.length || 0,
        active,
        pending,
        completed,
        cancelled,
        thisMonth,
        lastMonth,
        avgDurationDays: avgDuration,
      });
    } catch (error) {
      console.error("Error fetching placement stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIndicator = () => {
    if (stats.thisMonth > stats.lastMonth) {
      const increase = stats.lastMonth > 0 
        ? Math.round(((stats.thisMonth - stats.lastMonth) / stats.lastMonth) * 100)
        : 100;
      return {
        icon: TrendingUp,
        color: "text-green-500",
        text: `+${increase}% vs last month`
      };
    } else if (stats.thisMonth < stats.lastMonth) {
      const decrease = Math.round(((stats.lastMonth - stats.thisMonth) / stats.lastMonth) * 100);
      return {
        icon: TrendingDown,
        color: "text-red-500",
        text: `-${decrease}% vs last month`
      };
    }
    return {
      icon: Minus,
      color: "text-muted-foreground",
      text: "Same as last month"
    };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Placement Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const trend = getTrendIndicator();
  const TrendIcon = trend.icon;
  const occupancyRate = stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Home className="h-5 w-5" />
          Placement Statistics
        </CardTitle>
        <CardDescription>Housing placement overview and trends</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <p className="text-3xl font-bold text-primary">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Placements</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            <p className="text-xs text-muted-foreground">Currently Active</p>
          </div>
        </div>

        {/* This Month Trend */}
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div>
            <p className="text-sm font-medium">This Month</p>
            <p className="text-2xl font-bold">{stats.thisMonth}</p>
          </div>
          <div className={`flex items-center gap-1 text-sm ${trend.color}`}>
            <TrendIcon className="h-4 w-4" />
            <span>{trend.text}</span>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Status Breakdown</p>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Active</span>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
              {stats.active}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span>Pending</span>
            </div>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
              {stats.pending}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-blue-500" />
              <span>Completed</span>
            </div>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
              {stats.completed}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <span>Cancelled</span>
            </div>
            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400">
              {stats.cancelled}
            </Badge>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="pt-3 border-t space-y-3">
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Active Rate</span>
              <span className="font-medium">{occupancyRate}%</span>
            </div>
            <Progress value={occupancyRate} className="h-2" />
          </div>
          
          {stats.avgDurationDays > 0 && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Avg. Duration</span>
              </div>
              <span className="font-medium">{stats.avgDurationDays} days</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
